import { AlertCircle, TrendingDown, Clock, Users, ArrowRight, CheckCircle, Target, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const B2BChallenges = () => {
  const challenges = [
    {
      icon: TrendingDown,
      title: "Unpredictable Pipeline",
      description: "Revenue forecasting is guesswork because lead flow varies wildly month to month."
    },
    {
      icon: Clock,
      title: "Long Sales Cycles",
      description: "Deals stall because prospects aren't properly nurtured before reaching sales."
    },
    {
      icon: Users,
      title: "Low-Quality Leads",
      description: "Marketing generates volume but sales wastes time on unqualified prospects."
    },
    {
      icon: AlertCircle,
      title: "Competitor Positioning",
      description: "Better-positioned competitors win deals you should be closing."
    }
  ];

  const solutions = [
    {
      icon: Target,
      title: "Predictable Pipeline",
      description: "Data-driven campaigns that deliver consistent, qualified opportunities every month."
    },
    {
      icon: Zap,
      title: "Accelerated Velocity",
      description: "Multi-touch nurturing that moves prospects through your funnel faster."
    },
    {
      icon: CheckCircle,
      title: "Qualified Opportunities",
      description: "Lead scoring and ABM strategies that prioritize accounts ready to buy."
    },
    {
      icon: TrendingDown,
      title: "Market Leadership",
      description: "Strategic positioning that makes you the obvious choice in your category."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              B2B Growth Challenges We Solve
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              If your growth feels unpredictable, you are not alone. These are the patterns we see across B2B companies.
            </p>
          </div>

          {/* Challenges Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {challenges.map((challenge, index) => {
              const IconComponent = challenge.icon;
              return (
                <Card key={index} className="border-destructive/20 bg-destructive/5">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-destructive/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-destructive" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground mb-2">{challenge.title}</h3>
                        <p className="text-muted-foreground">{challenge.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-16">
            <div className="flex-1 h-px bg-border" />
            <span className="text-muted-foreground font-medium px-4">The VGP Solution</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Solutions Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {solutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <Card key={index} className="border-accent/20 bg-accent/5">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-secondary to-accent rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground mb-2">{solution.title}</h3>
                        <p className="text-muted-foreground">{solution.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button asChild size="lg" className="group">
              <a href="/book-call">
                Discuss Your Growth Challenges
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default B2BChallenges;
